import PropTypes from 'prop-types';
import ProvisionReasonAutocomplete, {
  ReasonTypes,
} from '@components/provisioning/custom-provision/ProvisionReasonAutocomplete';
import React, {useCallback, useEffect, useState} from 'react';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  JIRA_INVALID_MESSAGE,
  JIRA_KEY_RE,
  PL_NUMBER_INVALID_MESSAGE,
} from '@account/utils/errors';
import {Label} from '@components/common/Label';
import {PL_NUMBER_VALIDATION_REGEX} from '@account/utils';
import {createPVContract as _createPVContract} from '@account/api';
import {useMutation} from '@tanstack/react-query';

const ProvisioningDetailsStep = ({
  customerAccountId,
  isEmergencyOverride = false,
  onSuccess,
  onError,
  onLoadingChange,
}) => {
  const [reason, setReason] = useState(null);
  const [jira, setJira] = useState('');
  const [jiraTouched, setJiraTouched] = useState(false);
  const [jiraError, setJiraError] = useState(null);
  const [plNumber, setPlNumber] = useState('');
  const [plTouched, setPlTouched] = useState(false);
  const [plError, setPlError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const {mutate, isLoading} = useMutation(_createPVContract, {
    onSuccess: data => {
      if (!data?.id) {
        onError?.(new Error('Contract was created but no ID was returned.'));
        return;
      }
      onSuccess(data.id);
    },
    onError: error => onError?.(error),
  });

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const handleJiraChange = useCallback(
    e => {
      const val = e.target.value.toUpperCase();
      setJiraTouched(true);
      if (jiraError && JIRA_KEY_RE.test(val)) setJiraError(null);
      setJira(val);
    },
    [jiraError]
  );

  const handleJiraBlur = useCallback(() => {
    if (jira && !JIRA_KEY_RE.test(jira)) setJiraError(JIRA_INVALID_MESSAGE);
  }, [jira]);

  const handlePlChange = useCallback(
    e => {
      const val = e.target.value;
      setPlTouched(true);
      if (plError && PL_NUMBER_VALIDATION_REGEX.test(val)) setPlError(null);
      setPlNumber(val);
    },
    [plError]
  );

  const handlePlBlur = useCallback(() => {
    if (plNumber && !PL_NUMBER_VALIDATION_REGEX.test(plNumber))
      setPlError(PL_NUMBER_INVALID_MESSAGE);
  }, [plNumber]);

  const isTestContract = reason === ReasonTypes.TEST_CONTRACT;
  const jiraRequired = isEmergencyOverride || !isTestContract;
  const jiraValid = !jiraRequired || JIRA_KEY_RE.test(jira);

  const canSubmit =
    !!reason &&
    jiraValid &&
    !jiraError &&
    !plError &&
    (!isEmergencyOverride || confirmed);

  const handleSubmit = useCallback(() => {
    const contractName = isEmergencyOverride
      ? `EMERGENCY: ${jira}`
      : jira || reason;
    const reasonString = isEmergencyOverride
      ? `EMERGENCY: ${jira}: ${reason}`
      : jira
        ? `${jira}: ${reason}`
        : reason;
    mutate({
      customerAccountId,
      contractName,
      reason: reasonString,
      plNumber: plNumber || 'PL-OTHER',
    });
  }, [mutate, customerAccountId, isEmergencyOverride, jira, reason, plNumber]);

  return (
    <Stack spacing={2}>
      {isEmergencyOverride && (
        <Alert severity="warning">
          Emergency override — changes bypass Salesforce. Document this action
          carefully.
        </Alert>
      )}

      <Stack spacing={1}>
        <Label isInputLabel htmlFor="prov-reason">
          Reason
        </Label>
        <ProvisionReasonAutocomplete onChange={setReason} size="small" />
      </Stack>

      <Stack spacing={1}>
        <Label isInputLabel htmlFor="prov-jira">
          Jira Ticket{' '}
          {!jiraRequired && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
            >
              (optional for Test Contract)
            </Typography>
          )}
        </Label>
        <TextField
          autoComplete="off"
          error={jiraTouched && !!jiraError}
          fullWidth
          helperText={jiraTouched && jiraError}
          id="prov-jira"
          onBlur={handleJiraBlur}
          onChange={handleJiraChange}
          placeholder="CGO-1234"
          required={jiraRequired}
          size="small"
          value={jira}
        />
      </Stack>

      <Stack spacing={1}>
        <Label isInputLabel htmlFor="prov-pl">
          PL Number{' '}
          <Typography component="span" variant="caption" color="text.secondary">
            (optional — defaults to PL-OTHER)
          </Typography>
        </Label>
        <TextField
          autoComplete="off"
          error={plTouched && !!plError}
          fullWidth
          helperText={plTouched && plError}
          id="prov-pl"
          onBlur={handlePlBlur}
          onChange={handlePlChange}
          placeholder="PL-XXXXXXX"
          size="small"
          value={plNumber}
        />
      </Stack>

      {isEmergencyOverride && (
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
              size="small"
            />
          }
          label="I confirm this is a customer emergency requiring immediate action"
        />
      )}

      <Button
        color="primary"
        disabled={!canSubmit || isLoading}
        fullWidth
        onClick={handleSubmit}
        variant="contained"
      >
        {isLoading ? 'Creating…' : 'Continue'}
      </Button>
    </Stack>
  );
};

ProvisioningDetailsStep.propTypes = {
  customerAccountId: PropTypes.string.isRequired,
  isEmergencyOverride: PropTypes.bool,
  onError: PropTypes.func,
  onLoadingChange: PropTypes.func,
  onSuccess: PropTypes.func.isRequired,
};

export default ProvisioningDetailsStep;
